import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * Fetch a page's structured content from `/api/public-v2/pages/{slug}`.
 *
 * Failure mode is intentional: when the API call fails (server down,
 * page not yet authored in the admin) the hook returns `null` and the
 * consumer falls back to the legacy `mock.js` data — so the public site
 * never breaks while we migrate page-by-page from mocks to API.
 */
export function usePageContent(slug) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('mock');

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/api/public-v2/pages/${slug}`)
      .then((res) => {
        if (cancelled) return;
        setPage(res.data || null);
        setSource('api');
      })
      .catch(() => {
        if (cancelled) return;
        setPage(null);
        setSource('mock');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { page, loading, source };
}

/**
 * Pull a single section's fields from the page object as a flat
 * `{ fieldKey: value }` map. Returns null if the section is absent
 * or disabled — the caller should use its fallback in that case.
 */
export function getSectionFields(page, sectionType) {
  if (!page?.sections) return null;
  const section = page.sections.find((s) => s.type === sectionType && s.enabled !== false);
  if (!section) return null;
  return Object.fromEntries((section.fields || []).map((f) => [f.key, f.value]));
}
